/* eslint-disable consistent-return */
// @ts-nocheck

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
// styles
import ComparisonCheckbox from '@jo-v1/components/CompareCheckbox';
import { SchoolConfigData } from '@jo-v1/components/ExitStrategy/type';
import ProductFlags from '@jo-v2/components/ProductFlags';
import ProductTags from '@jo-v2/components/ProductTags';
import Testimonial from '@jo-v2/components/Testimonial';
import SchoolProgramTabs, { SchoolPanel } from '@jo-v2/features/JoRecommender/ProgramSelect/SchoolProgramTabs';
import { useEffect, useRef, useState, useMemo, memo, type MouseEventHandler } from 'react';
import { FaStar, FaThumbsUp } from 'react-icons/fa';
import { HiLocationMarker } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useProductEvents from '@core/hooks/cohesion/useProductEvents';
// selectors, actions
import {
  selectRecommenderPopupListId,
  setHeclidMapEntry,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
} from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import {
  setLeadDelivery,
  selectLeadDelivery,
  setCurrentProgram,
  selectSubmissions,
  setShowDuplicatePopup,
} from '@core/reducers/matchesSlice';
import { mobiusRedirect } from '@core/services/mobius';
import { getSchoolConfig } from '@core/services/schoolConfig';
import { VoyagerResult } from '@core/ts/results';
// hooks & utils
import cn from '@core/utils/classNames';
import getToken from '@core/utils/getToken';
import { highLightKeywords, paragraphToSentencesArray } from '@core/utils/strings/programDescriptions';

import DecisionFactorsFlags from '../DecisionFactorsFlags';
import KEYWORDS from '../RecommenderCard/keywords.json';
import styles from './AdditionalSchoolInfo.module.css';

interface ProgramPanelProps {
  programSnippet: string;
  onRecommender?: boolean;
}

export const ProgramPanel = ({ programSnippet, onRecommender }: ProgramPanelProps) => {
  const programSnippetWithHighlightedKeywords = highLightKeywords(programSnippet, KEYWORDS);
  const itemsList = paragraphToSentencesArray(programSnippetWithHighlightedKeywords);
  const singleItem = itemsList.length === 1;
  return (
    <div className={styles.programPanel}>
      <div>Why Consider This Program</div>
      <ul id="programTabPanel" className={cn(styles.programInfoList, onRecommender ? styles.onRecommender : '')}>
        {itemsList.map((bullet, idx) => (
          <li
            className={cn(singleItem ? styles.singleItem : '')}
            key={idx}
            dangerouslySetInnerHTML={{ __html: bullet }}
          />
        ))}
      </ul>
    </div>
  );
};

type ResultHighlightCardProps = {
  result: VoyagerResult;
  setRecommenderStep?: (step: string) => void;
  position?: number;
  setLoading?: (loading: boolean) => void;
  eventingOverrides?: any;
  index?: number;
  withTopBorder?: boolean;
  fullWidth?: boolean;
  onRecommender?: boolean;
};

const AdditionalSchoolInfo = ({
  result,
  setRecommenderStep,
  position,
  setLoading,
  eventingOverrides,
  index,
  fullWidth = false,
  withTopBorder = true,
  onRecommender = true,
}: ResultHighlightCardProps) => {
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // dispatches actions
  const dispatch = useDispatch();

  // local state
  const [schoolConfig, setSchoolConfig] = useState<SchoolConfigData>({});
  const [isSticky, setIsSticky] = useState(true);
  const [passedPlaceholder, setPassedPlaceholder] = useState(false);

  // refs for sticky behavior
  const ctaContainerRef = useRef<HTMLDivElement>(null);
  const ctaPlaceholderRef = useRef<HTMLDivElement>(null);

  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  // determine if we are in single submission
  // DEV NOTE: setting this to just be true for the purposes of a desired optimization
  // and we don't want to remove the multi-select code just yet
  const isSingleSubmissionRecommender = true;
  const flags = useFeatureFlags();
  const isComparisonTest = !!flags.programsComparisonTest;

  // Initialize events
  const { productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: onRecommender ? 'voyager-spotlight-pop-up' : 'voyager-recommender-embedded',
      formatType: isSingleSubmissionRecommender ? 'app' : 'multi-program',
      formatSubtype: 'jo-grid',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  // Setup sticky behavior
  useEffect(() => {
    if (!ctaContainerRef.current || !ctaPlaceholderRef.current) return;

    // Create an intersection observer to detect when placeholder is in/out of view
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // When the placeholder is visible, the element should not be sticky
          setIsSticky(false);
          setPassedPlaceholder(true);
        } else {
          // When placeholder is not visible
          // eslint-disable-next-line no-lonely-if
          if (!passedPlaceholder) {
            // If we haven't passed the placeholder yet, or we're back at the top
            // (initial load or scrolled all the way back up), element should be sticky
            setIsSticky(true);
          } else {
            // After we've passed the placeholder, keep it non-sticky when scrolling down
            setIsSticky(false);
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -76px 0px',
      }
    );

    observer.observe(ctaPlaceholderRef.current);

    // Reset the passed state when scrolling back to top
    const handleScroll = () => {
      if (window.scrollY === 0) {
        setPassedPlaceholder(false);
        setIsSticky(true);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // eslint-disable-next-line consistent-return
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [passedPlaceholder]);

  useEffect(() => {
    const getConfig = async () => {
      const schoolConfig = await getSchoolConfig(result?.school.id);
      setSchoolConfig(schoolConfig);
    };
    getConfig();
  }, [result]);

  const onBtnClickHandler = async (event) => {
    setLoading(true);
    dispatch(setCurrentProgram(result));

    const newProductCorrelationId = uuid();

    // add correlationId to map for LDS lead submission
    if (result?.program?.id) {
      dispatch(
        setProductCorrelationIdMapEntry({
          key: result.program.id,
          value: newProductCorrelationId,
        })
      );
    }

    productClicked({
      correlationId: newProductCorrelationId,
    });

    // Mobius Redirect will fire the OutcomeTracked APP_VIEWED event
    const { heclid } = await mobiusRedirect({
      idToken: getToken(),
      productCorrelationId: newProductCorrelationId,
      link: result?.url,
      trackingContextOverride: {
        formatType: 'app',
        formatSubtype: 'jo-grid',
      },
    });
    // add heclid to map for Mobius track if API call succeeds
    if (result?.program?.id) {
      dispatch(setHeclidMapEntry({ key: result.program.id, value: heclid }));
    }

    setLoading(false);
    setRecommenderStep('PII_CONFIRMATION');
  };

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const handleProductClick = () => {
    const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result?.school.id);
    // If school is already submitted, show duplicate popup
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: false,
          currentPrograms: [result],
          showExitStep: false,
        })
      );
      dispatch(setShowDuplicatePopup(true));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  const banner = document?.getElementById('joComparisonBanner');
  const [bannerHeight, setBannerHeight] = useState(isComparisonTest ? 174 : 0);

  useEffect(() => {
    if (!banner) return;

    setBannerHeight(banner.offsetHeight);

    const observer = new ResizeObserver(() => {
      setBannerHeight(banner.offsetHeight);
    });

    observer.observe(banner);
    return () => observer.disconnect();
  }, [banner]);

  const showDescription = true;

  return (
    <section className={cn(styles.card, fullWidth && styles.fullWidthCard, onRecommender && styles.onRecommender)}>
      {onRecommender && (
        <div>
          <div className={styles.programContainer}>
            <h3 className={styles.programName}>{result?.program?.name}</h3>
            <div className={styles.programDetails}>
              <ProductFlags flags={result?.school?.additional} />
            </div>
          </div>
          <div className={styles.spacer} />
        </div>
      )}
      <ProgramPanel programSnippet={result?.program?.snippet} onRecommender={onRecommender} />
      <div className={styles.spacer} />
      <Testimonial />
      <div className={styles.spacer} />
      {inputs?.importantFactors && (
        <div>
          <div className={styles.preferences}>
            Your Preferences
            <DecisionFactorsFlags decisionFactors={inputs?.importantFactors} showDescription />
          </div>
          <div className={styles.spacer} />
        </div>
      )}
      <SchoolPanel schoolAdditionalInfo={result?.school?.additional} centeredContent hasBorder />
      {!onRecommender && (
        <div className={styles.ctaStickyWrapper}>
          <div ref={ctaPlaceholderRef} className={styles.ctaPlaceholder} />
          {/* Sticky CTA container */}
          <div
            ref={ctaContainerRef}
            className={cn(styles.ctaContainer, isSticky ? styles.stickyCta : '')}
            style={{
              bottom: `${bannerHeight}px`,
            }}
          >
            <div>
              <button
                onClick={isSingleSubmissionRecommender && onRecommender ? onBtnClickHandler : handleProductClick}
                data-testid={`recommender-popup-card-button-${position}`}
                type="button"
                className={styles.cardBtnCta}
              >
                Explore Program
              </button>
              {isComparisonTest && (
                <span className={styles.comparisonCheckboxWrapperDesktop}>
                  <ComparisonCheckbox result={result} />
                </span>
              )}
            </div>
            <a href="#more-matches" onClick={() => setIsSticky(false)} className={styles.viewMoreMatches}>
              View more matches
            </a>
          </div>
        </div>
      )}
    </section>
  );
};

// We use memo to prevent rerenders when props and state haven't changed
export default memo(AdditionalSchoolInfo);
